/**
 * Lumyn Product Gallery
 * Interactive thumbnail gallery with scroll navigation for product pages
 */

class LumynProductGallery extends HTMLElement {
  constructor() {
    super();
    this.mainImage = null;
    this.thumbsContainer = null;
    this.thumbs = [];
    this.prevButton = null;
    this.nextButton = null;
    this.currentIndex = 0;
    this.mediaSources = new Map();
    this.preloadedImages = new Map();
    this.variantChangeUnsubscriber = undefined;
  }

  connectedCallback() {
    this.mainImage = this.querySelector('[data-main-image]');
    this.thumbsContainer = this.querySelector('[data-thumbs]');
    this.thumbs = Array.from(this.querySelectorAll('[data-thumb]'));
    this.prevButton = this.querySelector('[data-thumb-prev]');
    this.nextButton = this.querySelector('[data-thumb-next]');
    this.thumbsWrapper = this.querySelector('[data-thumbs-wrapper]');

    this.refreshMediaSources();
    this.preloadGalleryImages();

    if (!this.mainImage) {
      return;
    }

    const activeIndex = this.thumbs.findIndex((thumb) => thumb.classList.contains('is-active'));
    if (activeIndex >= 0) {
      this.currentIndex = activeIndex;
    }

    if (this.thumbsContainer && this.thumbs.length > 0) {
      this.bindEvents();
      this.updateArrowVisibility();
      this.setupResizeObserver();
    }

    const productInfo = this.closest('product-info');
    this.sectionId = productInfo?.dataset.section;

    if (typeof subscribe === 'function' && this.sectionId) {
      this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
        if (event.data?.sectionId !== this.sectionId) return;
        const featuredMediaId = event.data?.variant?.featured_media?.id;
        if (featuredMediaId) {
          this.setActiveMedia(featuredMediaId);
        }
      });
    }
  }

  disconnectedCallback() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    this.variantChangeUnsubscriber?.();
  }

  refreshMediaSources() {
    this.mediaSources.clear();
    this.querySelectorAll('.lumyn-product-hero__media-source[data-gallery-target]').forEach((source) => {
      const id = source.getAttribute('data-gallery-target');
      if (id) {
        this.mediaSources.set(id, source);
      }
    });
  }

  /**
   * Warm the browser cache for every gallery image so thumb switches are instant.
   */
  preloadGalleryImages() {
    this.mediaSources.forEach((source, id) => {
      if (this.preloadedImages.has(id)) return;

      const sourceImg = source.querySelector('img');
      if (!sourceImg) return;

      const src = sourceImg.getAttribute('src');
      if (!src) return;

      const loader = new Image();
      const srcset = sourceImg.getAttribute('srcset');
      const sizes = sourceImg.getAttribute('sizes');

      if (srcset) loader.srcset = srcset;
      if (sizes) loader.sizes = sizes;
      loader.alt = '';
      loader.decoding = 'async';
      loader.src = src;

      if (typeof loader.decode === 'function') {
        loader.decode().catch(() => {});
      }

      this.preloadedImages.set(id, loader);
    });
  }

  getMediaSource(mediaId) {
    const id = String(mediaId);
    if (this.mediaSources.has(id)) {
      return this.mediaSources.get(id);
    }
    const escapedId = typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(id) : id;
    return this.querySelector(`.lumyn-product-hero__media-source[data-gallery-target="${escapedId}"]`);
  }

  setActiveMedia(mediaId) {
    const id = String(mediaId);
    const index = this.thumbs.findIndex(
      (thumb) => thumb.getAttribute('data-gallery-target') === id
    );
    if (index >= 0) {
      this.selectThumb(index);
    }
  }

  bindEvents() {
    this.thumbs.forEach((thumb, index) => {
      thumb.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.selectThumb(index);
      });
      thumb.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          e.stopPropagation();
          this.selectThumb(index);
        }
      });
    });

    if (this.prevButton) {
      this.prevButton.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.scrollThumbs('prev');
      });
    }
    if (this.nextButton) {
      this.nextButton.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.scrollThumbs('next');
      });
    }

    this.thumbsContainer.addEventListener('scroll', () => {
      this.updateArrowVisibility();
    });

    this.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        this.navigateThumb(-1);
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        this.navigateThumb(1);
      }
    });

    this.setupTouchSupport();
  }

  setupTouchSupport() {
    let touchStartX = 0;
    let touchEndX = 0;

    this.mainImage.addEventListener(
      'touchstart',
      (e) => {
        touchStartX = e.changedTouches[0].screenX;
      },
      { passive: true }
    );

    this.mainImage.addEventListener(
      'touchend',
      (e) => {
        touchEndX = e.changedTouches[0].screenX;
        this.handleSwipe(touchStartX, touchEndX);
      },
      { passive: true }
    );
  }

  handleSwipe(startX, endX) {
    const swipeThreshold = 50;
    const diff = startX - endX;

    if (Math.abs(diff) < swipeThreshold) return;

    if (diff > 0) {
      this.navigateThumb(1);
    } else {
      this.navigateThumb(-1);
    }
  }

  setupResizeObserver() {
    this.resizeObserver = new ResizeObserver(() => {
      this.updateArrowVisibility();
    });
    this.resizeObserver.observe(this.thumbsContainer);
  }

  selectThumb(index) {
    if (index < 0 || index >= this.thumbs.length) return;

    this.currentIndex = index;
    const thumb = this.thumbs[index];
    const mediaId = thumb.getAttribute('data-gallery-target');

    this.thumbs.forEach((t, i) => {
      t.classList.toggle('is-active', i === index);
      t.setAttribute('aria-current', i === index ? 'true' : 'false');
    });

    this.showMedia(mediaId);
    this.scrollThumbIntoView(thumb);
    this.announceChange(index);
  }

  showMedia(mediaId) {
    if (!mediaId) return;

    this.refreshMediaSources();
    const source = this.getMediaSource(mediaId);
    if (!source) return;

    if (typeof window.pauseAllMedia === 'function') {
      window.pauseAllMedia();
    }

    const sourceImg = source.querySelector('img');
    if (sourceImg) {
      const preloaded = this.preloadedImages.get(String(mediaId));
      const src =
        (preloaded?.complete && (preloaded.currentSrc || preloaded.src)) ||
        sourceImg.getAttribute('src');
      if (!src) return;

      this.mainImage.querySelectorAll('video, model-viewer, iframe, .deferred-media').forEach((node) => {
        node.remove();
      });

      let mainImg = this.mainImage.querySelector('img');
      if (!mainImg) {
        mainImg = document.createElement('img');
        this.mainImage.replaceChildren(mainImg);
      }

      const srcset = sourceImg.getAttribute('srcset');
      const sizes = sourceImg.getAttribute('sizes');

      if (srcset) {
        mainImg.setAttribute('srcset', srcset);
      } else {
        mainImg.removeAttribute('srcset');
      }
      if (sizes) {
        mainImg.setAttribute('sizes', sizes);
      } else {
        mainImg.removeAttribute('sizes');
      }

      mainImg.src = src;
      mainImg.alt = sourceImg.getAttribute('alt') || '';
      if (sourceImg.width) mainImg.width = sourceImg.width;
      if (sourceImg.height) mainImg.height = sourceImg.height;
      mainImg.loading = 'eager';
      mainImg.decoding = 'sync';

      if (!mainImg.complete && typeof mainImg.decode === 'function') {
        mainImg.decode().catch(() => {});
      }
      return;
    }

    const fragment = document.createDocumentFragment();
    source.childNodes.forEach((node) => {
      if (node.nodeType === Node.ELEMENT_NODE || node.nodeType === Node.TEXT_NODE) {
        fragment.appendChild(node.cloneNode(true));
      }
    });

    if (!fragment.childNodes.length) return;

    this.mainImage.replaceChildren(fragment);

    const video = this.mainImage.querySelector('video');
    if (video) {
      video.muted = true;
      video.play().catch(() => {});
    }
  }

  navigateThumb(direction) {
    const newIndex = this.currentIndex + direction;
    if (newIndex >= 0 && newIndex < this.thumbs.length) {
      this.selectThumb(newIndex);
    }
  }

  scrollThumbs(direction) {
    const scrollAmount = this.thumbsContainer.clientWidth * 0.75;
    const currentScroll = this.thumbsContainer.scrollLeft;

    const newScroll =
      direction === 'prev' ? currentScroll - scrollAmount : currentScroll + scrollAmount;

    this.thumbsContainer.scrollTo({
      left: newScroll,
      behavior: 'smooth',
    });
  }

  scrollThumbIntoView(thumb) {
    const containerRect = this.thumbsContainer.getBoundingClientRect();
    const thumbRect = thumb.getBoundingClientRect();

    const isVisible =
      thumbRect.left >= containerRect.left && thumbRect.right <= containerRect.right;

    if (!isVisible) {
      thumb.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center',
      });
    }
  }

  updateArrowVisibility() {
    if (!this.thumbsWrapper) return;

    const { scrollLeft, scrollWidth, clientWidth } = this.thumbsContainer;
    const hasOverflow = scrollWidth > clientWidth + 1;
    const atStart = scrollLeft <= 1;
    const atEnd = scrollLeft >= scrollWidth - clientWidth - 1;

    this.thumbsWrapper.dataset.scrollHidden = !hasOverflow;

    if (this.prevButton) {
      this.prevButton.disabled = atStart;
      this.prevButton.setAttribute('aria-disabled', atStart ? 'true' : 'false');
    }

    if (this.nextButton) {
      this.nextButton.disabled = atEnd;
      this.nextButton.setAttribute('aria-disabled', atEnd ? 'true' : 'false');
    }
  }

  announceChange(index) {
    const statusElement = this.querySelector('[data-gallery-status]');
    if (statusElement) {
      statusElement.textContent = `Image ${index + 1} of ${this.thumbs.length}`;
    }
  }
}

if (!customElements.get('lumyn-product-gallery')) {
  customElements.define('lumyn-product-gallery', LumynProductGallery);
}

/**
 * Smooth scroll for "Learn More" button
 * Prevents navbar state changes during scroll by dispatching preventHeaderReveal
 */
document.addEventListener('DOMContentLoaded', () => {
  const learnMoreButtons = document.querySelectorAll('[data-scroll-to]');

  learnMoreButtons.forEach((button) => {
    button.addEventListener('click', (e) => {
      e.preventDefault();
      const targetId = button.dataset.scrollTo;
      const targetElement = document.querySelector(targetId);

      if (targetElement) {
        const stickyHeader = document.querySelector('sticky-header');
        if (stickyHeader) {
          stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
        }

        const header = document.querySelector('.header-wrapper');
        const headerHeight = header?.offsetHeight || 0;

        const targetRect = targetElement.getBoundingClientRect();
        const absoluteTargetTop = targetRect.top + window.scrollY;
        const targetPosition = Math.round(absoluteTargetTop - headerHeight - 24);

        window.scrollTo({
          top: targetPosition,
          behavior: 'smooth',
        });

        setTimeout(() => {
          targetElement.setAttribute('tabindex', '-1');
          targetElement.focus({ preventScroll: true });
        }, 500);
      }
    });
  });
});
